import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { environment } from '../../environments/environment';
import { DataService } from './data.service';
import { SnackbarService } from './snackbar.service';
import { firstValueFrom, Subscription } from 'rxjs';

export type NpsRange = 'detractor' | 'neutral' | 'promoter';

export interface NpsEligibility {
  eligible: boolean;
  cycleDays: number;
  lastResponseAt?: string | Date;
  nextEligibleAt?: string | Date;
}

export interface NpsSubmitResponse {
  response: {
    _id: string;
    score: number;
    range: NpsRange;
    comment?: string;
    createdAt: string;
  };
  eligibility: NpsEligibility;
}

@Injectable({
  providedIn: 'root'
})
export class NpsService {
  readonly visible = signal(false);
  readonly selectedScore = signal<number | null>(null);
  readonly comment = signal('');
  readonly submitting = signal(false);
  readonly thankYouVisible = signal(false);

  readonly scores = Array.from({ length: 11 }, (_, score) => score);
  readonly googleReviewUrl = (environment as any).npsGoogleReviewUrl || '';

  private readonly apiUrl = `${environment.mkgoURL}/api/v1/nps`;
  private readonly handledCompanies = new Set<string>();
  private readySubscription?: Subscription;
  private currentCompanyId?: string;

  constructor(
    private http: HttpClient,
    private dataService: DataService,
    private snackbar: SnackbarService
  ) { }

  initialize(): void {
    if (this.readySubscription) {
      return;
    }

    this.readySubscription = this.dataService.ready$.subscribe(([_user, company]) => {
      const companyId = company?.id;
      if (!companyId || companyId === this.currentCompanyId) {
        return;
      }

      this.currentCompanyId = companyId;
      this.visible.set(false);
      this.thankYouVisible.set(false);
      this.resetForm();
    });
  }

  async promptAfterImportantAction(): Promise<void> {
    const companyId = this.dataService.company?.id;
    if (!companyId || this.handledCompanies.has(companyId)) {
      return;
    }

    try {
      const options = await firstValueFrom(this.dataService.httpOptions(false));
      const eligibility = await firstValueFrom(this.http.get<NpsEligibility>(`${this.apiUrl}/check-eligibility`, options));

      if (!eligibility.eligible) {
        return;
      }

      this.openPrompt();
    } catch (error) {
      console.warn('Erro ao verificar elegibilidade NPS', error);
    }
  }

  selectScore(score: number): void {
    this.selectedScore.set(score);
  }

  setComment(value: string): void {
    this.comment.set(value);
  }

  dismiss(): void {
    const companyId = this.currentCompanyId ?? this.dataService.company?.id;
    if (companyId) {
      this.handledCompanies.add(companyId);
    }
    this.visible.set(false);
    this.thankYouVisible.set(false);
    this.resetForm();
  }

  async declinePermanently(): Promise<void> {
    const companyId = this.currentCompanyId ?? this.dataService.company?.id;

    try {
      const options = await firstValueFrom(this.dataService.httpOptions(false));
      await firstValueFrom(this.http.post<NpsEligibility>(`${this.apiUrl}/decline`, {}, options));
    } catch (error) {
      // A recusa persistente degrada para o comportamento efêmero: não bloqueamos o
      // usuário por falha de rede; o modal fecha e a sessão fica silenciada mesmo assim.
      console.warn('Erro ao registrar recusa de NPS', error);
    } finally {
      if (companyId) {
        this.handledCompanies.add(companyId);
      }
      this.visible.set(false);
      this.thankYouVisible.set(false);
      this.resetForm();
    }
  }

  async submit(): Promise<void> {
    const score = this.selectedScore();
    const companyId = this.currentCompanyId ?? this.dataService.company?.id;
    if (score === null || !companyId) {
      this.snackbar.error('Selecione uma nota para enviar.');
      return;
    }

    this.submitting.set(true);

    try {
      const options = await firstValueFrom(this.dataService.httpOptions(false));
      const result = await firstValueFrom(this.http.post<NpsSubmitResponse>(this.apiUrl, {
        score,
        comment: this.comment().trim(),
      }, options));

      this.handledCompanies.add(companyId);
      this.visible.set(false);
      this.snackbar.success('Obrigado por sua resposta.');

      if (result.response?.range === 'promoter' && this.googleReviewUrl) {
        this.thankYouVisible.set(true);
      } else {
        this.thankYouVisible.set(false);
        this.resetForm();
      }
    } catch (error: any) {
      if (error?.status === 409) {
        this.handledCompanies.add(companyId);
        this.visible.set(false);
        this.snackbar.success('Obrigado por sua resposta.');
        this.resetForm();
        return;
      }

      this.snackbar.error('Não foi possível enviar sua resposta agora.');
    } finally {
      this.submitting.set(false);
    }
  }

  closeThankYou(): void {
    this.thankYouVisible.set(false);
    this.resetForm();
  }

  private openPrompt(): void {
    this.resetForm();
    this.visible.set(true);
  }

  private resetForm(): void {
    this.selectedScore.set(null);
    this.comment.set('');
  }
}
